import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import Filter, { DisplayModeOption } from './filter';
import * as actions from './actions';
import {
    Map,
    TileLayer,
    WMSTileLayer,
    Polyline,
    Marker,
    CircleMarker,
    LayersControl,
    LayerGroup,
    ScaleControl,
    Circle
} from 'react-leaflet';
import FullscreenControl from 'react-leaflet-fullscreen';
import MeasureControl from 'react-leaflet-measure';
import CacheInfo from '../../cacheInfo';

const { BaseLayer, Overlay } = LayersControl;
const moduleName = 'gis-map';

const measureOptions = {
    position: 'topleft',
    primaryLengthUnit: 'meters',
    secondaryLengthUnit: 'kilometers',
    primaryAreaUnit: 'sqmeters',
    secondaryAreaUnit: 'acres',
    activeColor: '#db4a29',
    completedColor: '#9b2d14'
};

function mapStateToProps(state) {
    return {
        center: state[moduleName].center,
        zoom: state[moduleName].zoom,
        loaded: state[moduleName].loaded,
        projects: state[moduleName].projects,
        gisLabelDisplayZoom: state[moduleName].gisLabelDisplayZoom,
        linkedProjectsCount: state[moduleName].linkedProjectsCount,
        cacheKey: state[moduleName].cacheKey,
        cachedTime: state[moduleName].cachedTime,
        filterClosed: state[moduleName].filterClosed,
        selectedDisplayMode: state[moduleName].selectedDisplayMode,
    };
}

const mapDispatchToProps = {
    loadData: actions.loadData,
    loadLinkedData: actions.loadLinkedData,
    filterData: actions.filterData,
    filterAuxDrawings: actions.filterAuxDrawings,
    filterDataByDates: actions.filterDataByDates,
    toggleFilter: actions.toggleFilter,
    changeZoom: actions.zoomChanged,
    displayModeChanged: actions.displayModeChanged,
};

@connect(mapStateToProps, mapDispatchToProps)
class GisMap extends Component {

    static propTypes = {
        zoom: PropTypes.number,
        center: PropTypes.object,
        projects: PropTypes.array,
        loaded: PropTypes.bool,
        projectId: PropTypes.number.isRequired,
        gisLabelDisplayZoom: PropTypes.number,
        linkedProjectsCount: PropTypes.number,
        cacheKey: PropTypes.string,
        cachedTime: PropTypes.string,
        loadData: PropTypes.func.isRequired,
        loadLinkedData: PropTypes.func.isRequired,
        filterData: PropTypes.func.isRequired,
        filterAuxDrawings: PropTypes.func.isRequired,
        filterDataByDates: PropTypes.func.isRequired,
        toggleFilter: PropTypes.func,
        changeZoom: PropTypes.func,
        displayModeChanged: PropTypes.func.isRequired,
    };

    componentDidMount() {
        this.props.loadData(this.props.projectId);
        $('#myCollapsible').on('hidden.bs.collapse', function () {
            window.dispatchEvent(new Event('resize'));
        })
        this.props.loadLinkedData(this.props.projectId);
      }

    handleZoomEnd = (e) => {
        const target = e.target || e.leafletElement;
        this.props.changeZoom(target._zoom);
    }

    openProfile = (id) => {
        window.open(`/Profile/Edit/${id}`, '_blank');
    }

    openProfileGroup = (id) => {
        window.open(`/Profile/Group/${id}`, '_blank');
    }

    openExcavation = (id) => {
        window.open(`/Excavation/Edit/${id}`, '_blank');
    }

    openFaceFile = (id) => {
        window.open(`/FaceData/Download/${id}`, '_blank');
    }

    isValidProfileLine = (line) => line.startPoint && line.startPoint.wgs.isValid && line.endPoint && line.endPoint.wgs.isValid;

    filterAxisChainages = (axis) => {
        const filter = axis.gisShowChainageFilter;
        const filterN = axis.gisShowChainageFilterN;

        return axis.coordinates.filter((_, i) =>
            (filter == 0) || //every
            (filter == 1 && (i % filterN == 0 || i == 0 || i == axis.coordinates.length - 1)) || //every Nth + first + last
            (filter == 2) && (i == 0 || i == axis.coordinates.length - 1) // first + last
        )
    }

    getAxisLabel = (axis, zoom) => {
        const coord1 = axis.coordinates[Math.floor(axis.coordinates.length / 2)];
        const coord2 = axis.coordinates.length % 2 == 0 ? axis.coordinates[Math.floor((axis.coordinates.length / 2) - 1)] : coord1;
        return (zoom > this.props.gisLabelDisplayZoom && coord1) && <Marker position={{ lat: (coord1.lat + coord2.lat) / 2, lng: (coord1.lng + coord2.lng) / 2 }} icon={L.divIcon({ html: `<span class="gis-label">${axis.name}</span>` })} />;
    }

    getChainageLabel = (mode, position, zoom) => (zoom > this.props.gisLabelDisplayZoom) && <Marker position={position} icon={L.divIcon({ html: `<span class="gis-label to-left grey">${mode == 0 ? position.chainage : position.chainageFromTunnelZero}</span>` })} />

    getProfileLabel = (position, label, zoom, profileId, isTitle, hasGroup) => (zoom > this.props.gisLabelDisplayZoom && label != '') && <Marker onClick={() => (isTitle && hasGroup) ? this.openProfileGroup(profileId) : this.openProfile(profileId)} position={position} icon={L.divIcon({ html: `<span class="gis-label ${isTitle ? "gis-profile-title" : ""}">${label}</span>` })} />

    getFaceDataLabel = (position, label, zoom, id) => (zoom > this.props.gisLabelDisplayZoom) && <Marker onClick={() => this.openFaceFile(id)} position={position} icon={L.divIcon({ html: `<span class="gis-label">${label}</span>` })} />

    getExcavationLabel = (excavation, zoom) => (zoom > this.props.gisLabelDisplayZoom) && <Marker onClick={() => this.openExcavation(excavation.id)} position={excavation.arrowCoordinates.length > 0 ? excavation.arrowCoordinates[1] : excavation.coordinates[excavation.coordinates.length - 1]} icon={L.divIcon({ html: `<span title="${excavation.lastChainage} (${excavation.lastChainageDate})" class="gis-label">${excavation.name}</span>` })} />;

    renderAuxDrawings = (project) => {
        return project.auxDrawings.map((drawing) => {
            return (
                drawing.visible ?
                    <div key={`ad-${drawing.id}`}>
                        {drawing.polylines.map((polyline) => {
                            return (
                                <div key={`polyline-${polyline.id}`}>
                                    {polyline.lines.map((line) => {
                                        return (
                                            <div key={`line-${line.id}`}>
                                                <Polyline color={drawing.hexColor} positions={[line.startPoint.wgs, line.endPoint.wgs]} weight={drawing.weight} lineCap="butt" />
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                        {drawing.lines.map((line) => {
                            return (
                                <div key={`line-${line.id}`}>
                                    <Polyline color={drawing.hexColor} positions={[line.startPoint.wgs, line.endPoint.wgs]} weight={drawing.weight} lineCap="butt" />
                                </div>
                            )
                        })}
                        {drawing.circles.map((circle) => {
                            return (
                                <Circle key={`circle-${circle.id}`} center={circle.center.wgs} fillColor="transparent" color={drawing.hexColor} radius={circle.circleRadius} weight={drawing.weight} className={circle.id.toString()} />
                            )
                        })}
                    </div>
                : ''
            )
        })
    }

    renderExcavations = (axis, zoom) => {
        return (axis.showExcavations || axis.showTitle) ?
            <div>
                {axis.excavations.filter(e => axis.showExcavations).map((excavation) => {
                    return (excavation.coordinates.length > 1 &&
                        <div key={`ex-${excavation.id}`}>
                            <Polyline onClick={() => this.openExcavation(excavation.id)} color={excavation.color} positions={excavation.coordinates} weight={excavation.width} lineCap="butt" />
                            <Polyline onClick={() => this.openExcavation(excavation.id)} color={excavation.color} positions={excavation.arrowCoordinates} weight={excavation.width} lineCap="square" />
                            {this.getExcavationLabel(excavation, zoom)}
                        </div>
                    )
                })}
                {
                    axis.showTitle &&
                    <Polyline color={axis.color} positions={axis.coordinates} weight={2} lineCap="butt" />
                }
                {axis.showTitle && this.getAxisLabel(axis, zoom)}
                {this.filterAxisChainages(axis).filter(e => axis.showExcavations).map((coord) => {
                    return (
                        <div key={`ch-${coord.id}`}>
                            {this.getChainageLabel(axis.gisShowChainageMode, coord, zoom)}
                            <CircleMarker key={`axp-${coord.id}`} center={coord} color="black" radius={1} className={coord.id.toString()} />
                        </div>
                    )
                })}
            </div> 
        : ''
    }

    renderTitlePoint = (chapter, profile, zoom) => {
        return profile.titlePoint && <div>
            <Marker
                position={profile.titlePoint}
                onClick={() => this.openProfile(profile.id)}
                icon={L.divIcon({ html: `<span class="gis-icon material-icons nominal-point-shape-${chapter.shape}" style="color:${chapter.color}"></span>` })}>
                {this.getProfileLabel(profile.titlePoint, profile.name, zoom, profile.id, true, profile.hasGroup)}
            </Marker>
        </div>
    }

    renderTitleLine = (chapter, profile, zoom) => {
        return profile.titleLine && this.isValidProfileLine(profile.titleLine) && <div>
            <Polyline
                color={chapter.color}
                weight={5}
                positions={[profile.titleLine.startPoint, profile.titleLine.endPoint]}
                onClick={() => this.openProfile(profile.id)}>
            </Polyline>
            {this.getProfileLabel(
                {
                    lat: (profile.titleLine.startPoint.lat + profile.titleLine.endPoint.lat) / 2,
                    lng: (profile.titleLine.startPoint.lng + profile.titleLine.endPoint.lng) / 2
                },
                profile.name,
                zoom,
                profile.id,
                true,
                profile.hasGroup
            )}
        </div>
    }

    renderFaceData = (chapter, profile, zoom) => {
        return profile.faceData && profile.faceData.filter(d => d.coordinate != null).map((faceData) => {
            return (
                <div key={`fd-${faceData.id}`}>
                    {faceData.visible ?
                        <Marker
                            position={faceData.coordinate}
                            onClick={() => this.openProfile(profile.id)}
                            icon={L.divIcon({ html: `<span class="gis-icon material-icons nominal-point-shape-${chapter.shape}" style="color:${chapter.color}"></span>` })}>
                            {this.getFaceDataLabel(faceData.coordinate, faceData.name, zoom, faceData.id)}
                        </Marker> : ''
                    }
                </div>
            )
        })
    }

    renderLineData = (chapter, profile, zoom) => {
        return profile.line && this.isValidProfileLine(profile.line) && <div>
            <Polyline
                color={chapter.color}
                className={profile.line.alert ? "gis-point-pulse" : ""}
                weight={5}
                positions={[profile.line.startPoint, profile.line.endPoint]}
                onClick={() => this.openProfile(profile.id)}>
            </Polyline>
            {this.getProfileLabel(
                {
                    lat: (profile.line.startPoint.lat + profile.line.endPoint.lat) / 2,
                    lng: (profile.line.startPoint.lng + profile.line.endPoint.lng) / 2
                },
                profile.name,
                zoom,
                profile.id,
                false,
                false
            )}
        </div>
    }

    renderPolygonData = (chapter, profile, zoom) => {
        return profile.polygon && profile.polygon.map((line, index) => {
            if (this.isValidProfileLine(line)) {
                return (
                    <div key={`polygon-${line.startPoint.id}`}>
                        <Polyline
                            color={chapter.color}
                            className={line.alert ? "gis-point-pulse" : ""}
                            weight={5}
                            positions={[line.startPoint, line.endPoint]}
                            onClick={() => this.openProfile(profile.id)}>
                        </Polyline>
                        {this.getProfileLabel(
                            {
                                lat: (line.startPoint.lat + line.endPoint.lat) / 2,
                                lng: (line.startPoint.lng + line.endPoint.lng) / 2
                            },
                            (Math.floor(profile.polygon.length / 2) === index) ? profile.name : '',
                            zoom,
                            profile.id,
                            false,
                            false
                        )}
                    </div>
                )
            }
        })
    }

    renderPointsData = (chapter, profile, zoom, selectedDisplayMode) => {
        return profile.points && profile.points.map((point) => {
            return (
                <div key={`cm-${point.id}`}>
                    <Marker
                        position={point}
                        onClick={() => this.openProfile(profile.id)}
                        icon={L.divIcon({ html: `<span class="gis-icon material-icons nominal-point-shape-${chapter.shape} ${point.alert ? "gis-point-pulse" : ""}" style="color:${chapter.color}"></span>` })}>
                        {this.getProfileLabel(point, selectedDisplayMode === DisplayModeOption.NAME ? point.name : point.altDescription, zoom, profile.id, false, false)}
                    </Marker>
                </div>
            )
        })
    }

    renderProfiles = (chapter, zoom, selectedDisplayMode) => {
        return (
            chapter.profiles.map((profile) => {
                return (
                <div key={`pr-${profile.id}`}>
                    {
                        profile.visible &&
                        <div>
                            {this.renderTitlePoint(chapter, profile, zoom)}
                            {this.renderTitleLine(chapter, profile, zoom)}
                            {this.renderFaceData(chapter, profile, zoom)}
                            {this.renderLineData(chapter, profile, zoom)}
                            {this.renderPolygonData(chapter, profile, zoom)}
                            {this.renderPointsData(chapter, profile, zoom, selectedDisplayMode)}
                        </div>
                    }
                </div>
            )
        }))      
    }

    renderAxes = (project, zoom, selectedDisplayMode) => {
        return project.axes.map((axis) => {
            return (
                <div key={`ax-${axis.id}`}>
                    {this.renderExcavations(axis, zoom)}
                    {axis.chapters.map((chapter) => {
                        return this.renderProfiles(chapter, zoom, selectedDisplayMode)
                    })}
                </div>
            )
        })
    }

    renderGisProject = (project, zoom, selectedDisplayMode) => {
      return (
        <div key={`pad-${project.id}`}>
            {this.renderAuxDrawings(project)}
            {this.renderAxes(project, zoom, selectedDisplayMode)}
        </div>
    )
    }

    renderGisOverlay = (projects, zoom, selectedDisplayMode) => {
        return (
            <Overlay key={"GIS"} name={"GIS"} checked>
            <LayerGroup>
                <div>
                    {projects.map((project) => {
                        return (
                            this.renderGisProject(project, zoom, selectedDisplayMode)
                        )
                    })}
                </div>
            </LayerGroup>
        </Overlay>
        )
    }

    renderGoogleMapsLayer = () => {
        return (
            <BaseLayer checked name="Google Maps TMS">
                <LayerGroup>
                    {true && (
                        <TileLayer
                            attribution="Map data &amp;copy 2022 Google"
                            url={'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'}
                            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                            maxZoom={25}
                        />
                    )}
                </LayerGroup>
            </BaseLayer>
        )
    }

    renderGoogleMapsSatelliteLayer = () => {
        return (
            <BaseLayer name="Google Maps TMS Satellite">
                <LayerGroup>
                    {true && (
                        <TileLayer
                            attribution="Map data &amp;copy 2022 Google"
                            url={'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'}
                            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                            maxZoom={25}
                        />
                    )}
                </LayerGroup>
            </BaseLayer>
        )
    }

    renderOpenStreetLayer = () => {
        return (
            <BaseLayer name="Open Street Maps TMS">
                <LayerGroup>
                    {true && (
                        <TileLayer
                            attribution="Map data &amp;copy 2022 Google"
                            url={'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
                            maxZoom={25}
                        />
                    )}
                </LayerGroup>
            </BaseLayer>
        )
    }    

    renderCUZKOrtoFotoLayer = () => {
        return (
            <BaseLayer name="CUZK OrtoFoto WMS">
                {true && (
                    <WMSTileLayer
                        url={'http://geoportal.cuzk.cz/WMS_ORTOFOTO_PUB/WMService.aspx'}
                        layers={'GR_ORTFOTORGB'}
                    />
                )}
            </BaseLayer>
        )
    }    

    renderCUZK10000Layer = () => {
        return (
            <BaseLayer name="CUZK 1:10 000">
                {true && (
                    <WMSTileLayer
                        url={'http://geoportal.cuzk.cz/WMS_ZM10_PUB/WMService.aspx'}
                        layers={'GR_ZM10'}
                    />
                )}
            </BaseLayer>
        )
    }    

    renderLayers = (projects, zoom, selectedDisplayMode) => {
        return  (
            <LayersControl position="topright">
                {this.renderGisOverlay(projects, zoom, selectedDisplayMode)}
                {this.renderGoogleMapsLayer()}
                {this.renderGoogleMapsSatelliteLayer()}
                {this.renderOpenStreetLayer()}
                {this.renderCUZKOrtoFotoLayer()}
                {this.renderCUZK10000Layer()}
            </LayersControl>
        )
    }

    changeFix = () => {
        L.Control.Measure.include({
            // set icon on the capture marker
            _setCaptureMarkerIcon: function () {
                // disable autopan
                this._captureMarker.options.autoPanOnFocus = false;

                // default function
                this._captureMarker.setIcon(
                    L.divIcon({
                        iconSize: this._map.getSize().multiplyBy(2)
                    })
                );
            },
        });
    }

    render() {
        const { loaded, projects, filterData, filterAuxDrawings, filterDataByDates, filterClosed, toggleFilter, zoom, selectedDisplayMode, displayModeChanged, cachedTime, linkedProjectsCount, cacheKey } = this.props;

        return loaded ? (
                <div>
                {cachedTime && <CacheInfo cachedTime={cachedTime} cacheEntities={'ProjectGis,LinkedProjectsGis'} entityId={`${cacheKey},${cacheKey}`}></CacheInfo>}
                    <div style={{ width: '100%' }}>
                        <Map
                            bounds={projects && projects.map(p => p.axes).reduce((a, b) => a.concat(b)).map(a => a.coordinates).reduce((a, b) => a.concat(b))}
                            style={{ height: '685px', width: '100%' }}
                            onZoomEnd={this.handleZoomEnd}
                            ref={this.handleZoomEnd}
                        >
                            <FullscreenControl />
                            <MeasureControl {...measureOptions} />
                            {this.changeFix()}
                            <ScaleControl imperial={false} />
                            {this.renderLayers(projects, zoom, selectedDisplayMode)}
                        </Map>
                        <Filter
                            projects={projects}
                            filterData={filterData}
                            filterAuxDrawings={filterAuxDrawings}
                            filterDataByDates={filterDataByDates}
                            filterClosed={filterClosed}
                            toggleFilter={toggleFilter}
                            displayModeChanged={displayModeChanged}
                            linkedProjectsCount={linkedProjectsCount}
                            style={{ width: '0px', float: 'left' }}
                        />
                    </div>
            </div>
        ) : this.progressLoader()
    }

    progressLoader = () => (
        <div className="gis-map-loader">
            <div className="loader-center">
                <div className="graph-loading">
                    <div className="loader">
                        <div className="container">
                            <div className="dot dot-1"></div>
                            <div className="dot dot-2"></div>
                            <div className="dot dot-3"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>);
}

export default GisMap



// WEBPACK FOOTER //
// ./App/components/modules/gis-map/gis-map.js